import { UndoRedoResponse } from "@/types/action"
import { DayObject } from "@/types/date"
import { PlaceRef } from "@/types/entity"
import {
  ConfirmOption,
  CreateItineraryEventResponse,
  DayNote,
  DayTitle,
  Itinerary,
  ItineraryClearOnly,
  ItineraryComment,
  ItineraryDay,
  ItineraryEvent,
  ItineraryEventCreation,
  ItineraryEventUpdates,
  RemoveItineraryEventsResponse,
} from "@/types/itinerary"
import { LocationSearchHit } from "@/types/place"
import { Route } from "@/types/route"
import { Trip, TripRef } from "@/types/trip"
import Honeybadger from "@honeybadger-io/js"

import { errorMessage } from "@/lib/exception-utils"
import { ensureArray } from "@/lib/utils/collections"
import { AppDispatch } from "../redux-store"
import { baseApi } from "./base-api"
import { bookingsApi } from "./bookings-api"
import { tripsApi } from "./trips-api"

function invalidateTripsCalendarTag(dispatch: AppDispatch) {
  dispatch(tripsApi.util.invalidateTags(["TripsCalendar"]))
}

const COMMENT_PAGE_SIZES = [5, 50] as const

function updateCachedCommentCount(
  dispatch: AppDispatch,
  tripRef: TripRef,
  eventId: number,
  update: number | ((currentCount: number) => number)
) {
  return dispatch(
    itineraryApi.util.updateQueryData("getItinerary", { tripRef }, (draft) => {
      if (!draft) return
      const event = draft.events.find((e) => e.id === eventId)
      if (!event) return

      event.comment_count =
        typeof update === "function" ? update(event.comment_count) : update
    })
  )
}

function addCachedComment(
  dispatch: AppDispatch,
  tripRef: TripRef,
  eventId: number,
  comment: ItineraryComment,
  commentCount: number
) {
  return COMMENT_PAGE_SIZES.map((perPage) =>
    dispatch(
      itineraryApi.util.updateQueryData(
        "getComments",
        { tripRef, eventId, perPage },
        (draft) => {
          const comments = draft.comments.filter(
            (cachedComment) => cachedComment.id !== comment.id
          )
          comments.push(comment)
          draft.comments = comments.slice(-perPage)
          draft.comment_count = commentCount
        }
      )
    )
  )
}

function removeCachedComment(
  dispatch: AppDispatch,
  tripRef: TripRef,
  eventId: number,
  commentId: number
) {
  return COMMENT_PAGE_SIZES.map((perPage) =>
    dispatch(
      itineraryApi.util.updateQueryData(
        "getComments",
        { tripRef, eventId, perPage },
        (draft) => {
          draft.comments = draft.comments.filter(
            (comment) => comment.id !== commentId
          )
          draft.comment_count = Math.max(draft.comment_count - 1, 0)
        }
      )
    )
  )
}

/**
 * Cache Strategy:
 * - Manual updates (createEvent, updateEvent, removeEvents, removeEventsWithPlace):
 *   updates cache directly, avoids invalidatesTags to prevent refetch flashing
 * - Tag invalidation (other mutations): simpler, causes refetch
 */

export interface GetItineraryArgs {
  tripRef: TripRef
}

export interface GetItineraryEventsArgs {
  tripRef: TripRef
  eventIds: number[]
}

export interface GetCommentsArgs {
  tripRef: TripRef
  eventId: number
  perPage?: number
}

export interface CreateEventArgs {
  tripRef: TripRef
  params: ItineraryEventCreation
}

export interface UpdateEventArgs {
  tripRef: TripRef
  eventId: number
  updates: ItineraryEventUpdates
  confirmed?: ConfirmOption
}

export interface RemoveEventsArgs {
  tripRef: TripRef
  eventIds: number[]
}

export interface RemoveEventsWithPlaceArgs {
  tripRef: TripRef
  placeRef: PlaceRef | PlaceRef[]
}

export interface UpdateDayTitleArgs {
  tripRef: TripRef
  date: DayObject
  title: string | undefined
}

export interface UpdateDayNoteArgs {
  tripRef: TripRef
  date: DayObject
  note: string | undefined
}

export interface InsertDayArgs {
  tripRef: TripRef
  date: DayObject
  count?: number
}

export interface RemoveDayArgs {
  tripRef: TripRef
  date: DayObject
  count?: number
}

export interface CreateCommentArgs {
  tripRef: TripRef
  eventId: number
  text: string
}

export interface UpdateCommentArgs {
  tripRef: TripRef
  eventId: number
  commentId: number
  text: string
}

export interface RemoveCommentArgs {
  tripRef: TripRef
  eventId: number
  commentId: number
}

export interface ClearItineraryArgs {
  tripRef: TripRef
  only?: ItineraryClearOnly
}

export interface GetRouteArgs {
  tripRef: TripRef
}

export interface EventMutationResponse {
  event: ItineraryEvent
  itinerary?: Itinerary
  trip?: Trip
}

export interface ItineraryMutationResponse {
  itinerary: Itinerary
  trip?: Trip
}

export interface BestLocationByDayArgs {
  tripRef: TripRef
  itineraryDay: ItineraryDay
}

export interface BestLocationByDayResponse {
  location: LocationSearchHit
}

/** Optimistic cache update with automatic rollback on failure. */
async function withOptimisticUpdate<T>(
  dispatch: AppDispatch,
  tripRef: TripRef,
  updateFn: (draft: Itinerary) => void,
  queryFulfilled: Promise<{ data: T }>,
  onSuccess?: (data: T, dispatch: AppDispatch) => void,
  onError?: (error: unknown) => void
): Promise<void> {
  const patches: Array<{ undo: () => void }> = []

  try {
    const patch = dispatch(
      itineraryApi.util.updateQueryData(
        "getItinerary",
        { tripRef },
        (draft) => {
          if (!draft) return
          updateFn(draft)
        }
      )
    )
    patches.push(patch)
  } catch {
    // Cache doesn't exist yet
  }

  try {
    const result = await queryFulfilled
    onSuccess?.(result.data, dispatch)
  } catch (error) {
    patches.forEach((patch) => patch.undo())
    onError?.(error)
  }
}

export interface AddIdeasArgs {
  tripRef: TripRef
  entityRefs: string[]
}

export const itineraryApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getItinerary: builder.query<Itinerary, GetItineraryArgs>({
      query: ({ tripRef }) => ({
        url: `api/itineraries/${tripRef}`,
      }),
      transformResponse: (response: { itinerary: Itinerary }) =>
        response.itinerary,
      providesTags: (_result, _error, { tripRef }) => [
        { type: "Itinerary", id: tripRef },
      ],
    }),
    getItineraryEvents: builder.query<ItineraryEvent[], GetItineraryEventsArgs>(
      {
        query: ({ tripRef, eventIds }) => ({
          url: `api/itineraries/${tripRef}/event/${eventIds.join(",")}`,
        }),
        transformResponse: (response: { events: ItineraryEvent[] }) =>
          response.events,
        providesTags: (_result, _error, { tripRef }) => [
          { type: "ItineraryEvents", id: tripRef },
        ],
      }
    ),
    getComments: builder.query<
      { comments: ItineraryComment[]; comment_count: number },
      GetCommentsArgs
    >({
      query: ({ tripRef, eventId, perPage }) => {
        let url = `api/itineraries/${tripRef}/event/${eventId}/comments`
        if (perPage) {
          url = `${url}?per_page=${perPage}`
        }
        return { url }
      },
      providesTags: (_result, _error, { tripRef, eventId }) => [
        { type: "Comments", id: `${tripRef}-${eventId}` },
      ],
    }),
    getRoute: builder.query<Route | null, GetRouteArgs>({
      query: ({ tripRef }) => ({
        url: `api/itineraries/${tripRef}/route`,
      }),
      transformResponse: (response: { route?: Route }) =>
        response.route ?? null,
      providesTags: (_result, _error, { tripRef }) => [
        { type: "Route", id: tripRef },
      ],
    }),
    createEvent: builder.mutation<
      CreateItineraryEventResponse,
      CreateEventArgs
    >({
      query: ({ tripRef, params }) => ({
        url: `api/itineraries/${tripRef}/event`,
        method: "POST",
        data: params,
      }),
      async onQueryStarted({ tripRef, params }, { dispatch, queryFulfilled }) {
        try {
          const result = await queryFulfilled
          if (result.data.event) {
            dispatch(
              itineraryApi.util.updateQueryData(
                "getItinerary",
                { tripRef },
                (draft) => {
                  if (!draft) return
                  const index = draft.events.findIndex(
                    (e) => e.id === result.data.event.id
                  )
                  if (index === -1) {
                    draft.events.unshift(result.data.event)
                  } else {
                    draft.events[index] = result.data.event
                  }
                }
              )
            )
          }
          if (params.entity_ref) {
            dispatch(
              tripsApi.util.invalidateTags([
                { type: "PlaceTripRefs", id: params.entity_ref },
              ])
            )
          }
          dispatch(
            itineraryApi.util.invalidateTags([{ type: "Route", id: tripRef }])
          )
          invalidateTripsCalendarTag(dispatch)
        } catch (error) {
          Honeybadger.addBreadcrumb("createEvent cache update failed", {
            category: "error",
            metadata: { tripRef, error: errorMessage(error) },
          })
        }
      },
    }),
    updateEvent: builder.mutation<EventMutationResponse, UpdateEventArgs>({
      query: ({ tripRef, eventId, updates, confirmed }) => ({
        url: `api/itineraries/${tripRef}/event/${eventId}`,
        method: "PUT",
        data: {
          event: updates,
          confirmed: confirmed,
        },
      }),
      async onQueryStarted({ tripRef, eventId }, { dispatch, queryFulfilled }) {
        try {
          const result = await queryFulfilled
          if (result.data.event) {
            dispatch(
              itineraryApi.util.updateQueryData(
                "getItinerary",
                { tripRef },
                (draft) => {
                  if (!draft) return
                  const index = draft.events.findIndex((e) => e.id === eventId)
                  if (index !== -1) {
                    draft.events[index] = result.data.event
                  }
                }
              )
            )
          }
          dispatch(
            itineraryApi.util.invalidateTags([{ type: "Route", id: tripRef }])
          )
          invalidateTripsCalendarTag(dispatch)
        } catch (error) {
          Honeybadger.addBreadcrumb("updateEvent cache update failed", {
            category: "error",
            metadata: { tripRef, eventId, error: errorMessage(error) },
          })
        }
      },
    }),
    removeEvents: builder.mutation<
      RemoveItineraryEventsResponse,
      RemoveEventsArgs
    >({
      query: ({ tripRef, eventIds }) => ({
        url: `api/itineraries/${tripRef}/event/${eventIds.join(",")}`,
        method: "DELETE",
      }),
      async onQueryStarted(
        { tripRef, eventIds },
        { dispatch, queryFulfilled }
      ) {
        await withOptimisticUpdate(
          dispatch,
          tripRef,
          (draft) => {
            draft.events = draft.events.filter((e) => !eventIds.includes(e.id))
          },
          queryFulfilled,
          (data, dispatch) => {
            const affectedPlaceRefs = data.events
              .map((e) => e.place?.entity_ref)
              .filter((ref): ref is PlaceRef => ref != null)
            if (affectedPlaceRefs.length > 0) {
              dispatch(
                tripsApi.util.invalidateTags(
                  affectedPlaceRefs.map((ref) => ({
                    type: "PlaceTripRefs",
                    id: ref,
                  }))
                )
              )
            }
            dispatch(
              itineraryApi.util.invalidateTags([{ type: "Route", id: tripRef }])
            )
            dispatch(bookingsApi.util.invalidateTags(["Bookings"]))
            invalidateTripsCalendarTag(dispatch)
          },
          (error) => {
            Honeybadger.addBreadcrumb("removeEvents failed, reverted cache", {
              category: "error",
              metadata: { tripRef, eventIds, error: errorMessage(error) },
            })
          }
        )
      },
    }),
    removeEventsWithPlace: builder.mutation<
      RemoveItineraryEventsResponse,
      RemoveEventsWithPlaceArgs
    >({
      query: ({ tripRef, placeRef }) => {
        const placeRefs = ensureArray(placeRef)
        return {
          url: `api/itineraries/${tripRef}/place/${placeRefs.join(",")}`,
          method: "DELETE",
        }
      },
      async onQueryStarted(
        { tripRef, placeRef },
        { dispatch, queryFulfilled }
      ) {
        const placeRefs = ensureArray(placeRef)

        await withOptimisticUpdate(
          dispatch,
          tripRef,
          (draft) => {
            draft.events = draft.events.filter(
              (e) => !e.place || !placeRefs.includes(e.place.entity_ref)
            )
          },
          queryFulfilled,
          (_data, dispatch) => {
            dispatch(
              tripsApi.util.invalidateTags(
                placeRefs.map((ref) => ({ type: "PlaceTripRefs", id: ref }))
              )
            )
            dispatch(
              itineraryApi.util.invalidateTags([{ type: "Route", id: tripRef }])
            )
            dispatch(bookingsApi.util.invalidateTags(["Bookings"]))
            invalidateTripsCalendarTag(dispatch)
          },
          (error) => {
            Honeybadger.addBreadcrumb(
              "removeEventsWithPlace failed, reverted cache",
              {
                category: "error",
                metadata: { tripRef, placeRefs, error: errorMessage(error) },
              }
            )
          }
        )
      },
    }),
    updateDayTitle: builder.mutation<
      { day_titles: DayTitle[]; itinerary?: Itinerary },
      UpdateDayTitleArgs
    >({
      query: ({ tripRef, date, title }) => ({
        url: `api/itineraries/${tripRef}/day_title`,
        method: "PUT",
        data: { date, title },
      }),
      invalidatesTags: (_result, _error, { tripRef }) => [
        { type: "Itinerary", id: tripRef },
      ],
    }),
    updateDayNote: builder.mutation<
      { day_notes?: DayNote[]; itinerary?: Itinerary },
      UpdateDayNoteArgs
    >({
      query: ({ tripRef, date, note }) => ({
        url: `api/itineraries/${tripRef}/day_note`,
        method: "PUT",
        data: { date, note },
      }),
      invalidatesTags: (_result, _error, { tripRef }) => [
        { type: "Itinerary", id: tripRef },
      ],
    }),
    insertDay: builder.mutation<
      { trip: Trip; itinerary?: Itinerary },
      InsertDayArgs
    >({
      query: ({ tripRef, date, count }) => ({
        url: `api/itineraries/${tripRef}/insert_day`,
        method: "POST",
        data: { date, count },
      }),
      invalidatesTags: (_result, _error, { tripRef }) => [
        { type: "Itinerary", id: tripRef },
      ],
    }),
    removeDay: builder.mutation<
      { trip: Trip; itinerary?: Itinerary },
      RemoveDayArgs
    >({
      query: ({ tripRef, date, count }) => ({
        url: `api/itineraries/${tripRef}/remove_day`,
        method: "POST",
        data: { date, count },
      }),
      invalidatesTags: (_result, _error, { tripRef }) => [
        { type: "Itinerary", id: tripRef },
      ],
    }),
    createComment: builder.mutation<
      { comment: ItineraryComment; comment_count: number },
      CreateCommentArgs
    >({
      query: ({ tripRef, eventId, text }) => ({
        url: `api/itineraries/${tripRef}/event/${eventId}/comments`,
        method: "POST",
        data: { comment: { text } },
      }),
      async onQueryStarted({ tripRef, eventId }, { dispatch, queryFulfilled }) {
        try {
          const result = await queryFulfilled
          addCachedComment(
            dispatch,
            tripRef,
            eventId,
            result.data.comment,
            result.data.comment_count
          )
          updateCachedCommentCount(
            dispatch,
            tripRef,
            eventId,
            result.data.comment_count
          )
        } catch (error) {
          Honeybadger.addBreadcrumb("createComment cache update failed", {
            category: "error",
            metadata: { tripRef, eventId, error: errorMessage(error) },
          })
        }
      },
      invalidatesTags: (_result, _error, { tripRef, eventId }) => [
        { type: "Comments", id: `${tripRef}-${eventId}` },
      ],
    }),
    updateComment: builder.mutation<
      { comment: ItineraryComment },
      UpdateCommentArgs
    >({
      query: ({ tripRef, eventId, commentId, text }) => ({
        url: `api/itineraries/${tripRef}/event/${eventId}/comments/${commentId}`,
        method: "PUT",
        data: { comment: { text } },
      }),
      invalidatesTags: (_result, _error, { tripRef, eventId }) => [
        { type: "Comments", id: `${tripRef}-${eventId}` },
      ],
    }),
    removeComment: builder.mutation<null, RemoveCommentArgs>({
      query: ({ tripRef, eventId, commentId }) => ({
        url: `api/itineraries/${tripRef}/event/${eventId}/comments/${commentId}`,
        method: "DELETE",
      }),
      transformResponse: () => null,
      async onQueryStarted(
        { tripRef, eventId, commentId },
        { dispatch, queryFulfilled }
      ) {
        const countPatchResult = updateCachedCommentCount(
          dispatch,
          tripRef,
          eventId,
          (commentCount) => Math.max(commentCount - 1, 0)
        )
        const commentPatchResults = removeCachedComment(
          dispatch,
          tripRef,
          eventId,
          commentId
        )

        try {
          await queryFulfilled
        } catch (error) {
          countPatchResult.undo()
          commentPatchResults.forEach((patchResult) => patchResult.undo())
          Honeybadger.addBreadcrumb("removeComment cache update failed", {
            category: "error",
            metadata: { tripRef, eventId, error: errorMessage(error) },
          })
        }
      },
      invalidatesTags: (_result, _error, { tripRef, eventId }) => [
        { type: "Comments", id: `${tripRef}-${eventId}` },
      ],
    }),
    undoItinerary: builder.mutation<UndoRedoResponse, TripRef>({
      query: (tripRef) => ({
        url: `api/itineraries/${tripRef}/undo`,
        method: "POST",
      }),
      async onQueryStarted(_tripRef, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled
          invalidateTripsCalendarTag(dispatch)
        } catch {
          // ignore
        }
      },
      invalidatesTags: (_result, _error, tripRef) => [
        { type: "Itinerary", id: tripRef },
        { type: "ItineraryEvents", id: tripRef },
        { type: "Route", id: tripRef },
      ],
    }),
    redoItinerary: builder.mutation<UndoRedoResponse, TripRef>({
      query: (tripRef) => ({
        url: `api/itineraries/${tripRef}/redo`,
        method: "POST",
      }),
      async onQueryStarted(_tripRef, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled
          invalidateTripsCalendarTag(dispatch)
        } catch {
          // ignore
        }
      },
      invalidatesTags: (_result, _error, tripRef) => [
        { type: "Itinerary", id: tripRef },
        { type: "ItineraryEvents", id: tripRef },
        { type: "Route", id: tripRef },
      ],
    }),
    clearItinerary: builder.mutation<
      { itinerary: Itinerary },
      ClearItineraryArgs
    >({
      query: ({ tripRef, only }) => {
        const baseUrl = `api/itineraries/${tripRef}/clear`
        const urlParams = new URLSearchParams()
        if (only) {
          urlParams.append("only", only)
        }
        const url = urlParams.toString()
          ? `${baseUrl}?${urlParams.toString()}`
          : baseUrl
        return {
          url,
          method: "POST",
        }
      },
      async onQueryStarted(args, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled
          dispatch(tripsApi.util.invalidateTags(["PlaceTripRefs"]))
          dispatch(bookingsApi.util.invalidateTags(["Bookings"]))
          invalidateTripsCalendarTag(dispatch)
        } catch (error) {
          Honeybadger.addBreadcrumb(
            "clearItinerary cache invalidation failed",
            {
              category: "error",
              metadata: { tripRef: args.tripRef, error: errorMessage(error) },
            }
          )
        }
      },
      invalidatesTags: (_result, _error, { tripRef }) => [
        { type: "Itinerary", id: tripRef },
        { type: "ItineraryEvents", id: tripRef },
        { type: "Route", id: tripRef },
      ],
    }),
    unscheduleEvents: builder.mutation<{ itinerary: Itinerary }, TripRef>({
      query: (tripRef) => ({
        url: `api/itineraries/${tripRef}/unschedule_events`,
        method: "POST",
      }),
      async onQueryStarted(_tripRef, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled
          invalidateTripsCalendarTag(dispatch)
        } catch {
          // ignore
        }
      },
      invalidatesTags: (_result, _error, tripRef) => [
        { type: "Itinerary", id: tripRef },
        { type: "ItineraryEvents", id: tripRef },
        { type: "Route", id: tripRef },
      ],
    }),
    addIdeas: builder.mutation<
      { added_count: number; events: ItineraryEvent[]; itinerary: Itinerary },
      AddIdeasArgs
    >({
      query: ({ tripRef, entityRefs }) => ({
        url: `api/itineraries/${tripRef}/ideas`,
        method: "POST",
        data: {
          entity_refs: entityRefs,
        },
      }),
      async onQueryStarted(
        { tripRef, entityRefs },
        { dispatch, queryFulfilled }
      ) {
        try {
          await queryFulfilled
          // Invalidate PlaceTripRefs for all added places
          if (entityRefs.length > 0) {
            dispatch(
              tripsApi.util.invalidateTags(
                entityRefs.map((ref) => ({ type: "PlaceTripRefs", id: ref }))
              )
            )
          }
          invalidateTripsCalendarTag(dispatch)
        } catch (error) {
          Honeybadger.addBreadcrumb(
            "addIdeasToItinerary cache invalidation failed",
            {
              category: "error",
              metadata: { tripRef, entityRefs, error: errorMessage(error) },
            }
          )
        }
      },
      invalidatesTags: (_result, _error, { tripRef }) => [
        { type: "Itinerary", id: tripRef },
        { type: "ItineraryEvents", id: tripRef },
      ],
    }),
    bestLocationByItineraryDay: builder.query<
      BestLocationByDayResponse,
      BestLocationByDayArgs
    >({
      query: ({ tripRef, itineraryDay }) => ({
        url: `api/itineraries/${tripRef}/best_location`,
        params: {
          day_num: itineraryDay.dayNumber,
        },
      }),
    }),
  }),
})

export const {
  useGetItineraryQuery,
  useLazyGetItineraryQuery,
  useGetItineraryEventsQuery,
  useLazyGetItineraryEventsQuery,
  useGetCommentsQuery,
  useLazyGetCommentsQuery,
  useGetRouteQuery,
  useLazyGetRouteQuery,
  useCreateEventMutation,
  useUpdateEventMutation,
  useRemoveEventsMutation,
  useRemoveEventsWithPlaceMutation,
  useUpdateDayTitleMutation,
  useUpdateDayNoteMutation,
  useInsertDayMutation,
  useRemoveDayMutation,
  useCreateCommentMutation,
  useUpdateCommentMutation,
  useRemoveCommentMutation,
  useUndoItineraryMutation,
  useRedoItineraryMutation,
  useClearItineraryMutation,
  useUnscheduleEventsMutation,
  useAddIdeasMutation,
  useBestLocationByItineraryDayQuery,
} = itineraryApi
